import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { finalize } from "rxjs";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import MainContentBox from "src/Shared/Components/MainContentBox/MainContentBox";
import Title from "src/Shared/Components/Title/Title";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { IResult } from "src/Shared/Interfaces";
import { ToastService } from "src/Shared/Services";
import CommonService from "src/Shared/Services/Common.service";
import CompanyService from "../../Services/Company.service";
import { ClientCreationRoute } from "../../Enums/ClientCreationRoute.enum";
import { IRegion } from "../../Interfaces/IRegion";
import { fields } from "./fields";

type RouteOption = {
  route: ClientCreationRoute;
  title: string;
  description: string;
};

// The route decides status, Imperans link and landing place from the outset (P.5).
const ROUTE_OPTIONS: RouteOption[] = [
  {
    route: ClientCreationRoute.NewProspect,
    title: "New Prospect",
    description:
      "A lead to track. Created at Prospect status, not linked to Imperans; lands on the Prospects grid.",
  },
  {
    route: ClientCreationRoute.NewClientOnboarding,
    title: "New Client — Onboarding",
    description:
      "A client ready to onboard. Created at Onboarding status, not linked to Imperans; opens the onboarding wizard at step 1.",
  },
  {
    route: ClientCreationRoute.CurrentClient,
    title: "Current Client",
    description:
      "An existing live client. Created at Current Client status, linked to Imperans; lands on the Home grid.",
  },
];

const regionCountryHint = (region?: IRegion): string | null => {
  const country = region?.countryName || "";
  if (/zealand/i.test(country)) return "This is a New Zealand region.";
  if (/austral/i.test(country)) return "This is an Australian region.";
  return null;
};

const AddNewClient: React.FC = () => {
  const navigate = useNavigate();
  const form = useMemo(() => getFormValidation(fields), []);
  const getField = (key: string) => form.$(key);

  const {
    rootStore: {
      authStore: { user },
      companyStore: { resetCompanyList, getProspectsCount },
      commonStore: { supportiveRegions, industries, leadTypeList, loadCommonData },
      uiStore,
    },
  } = useStore();

  const [route, setRoute] = useState<ClientCreationRoute | null>(null);
  const [regions, setRegions] = useState<IRegion[]>([]);

  useEffect(() => {
    loadCommonData();
    CommonService.getRegions().subscribe({
      next: (data) => setRegions(data || []),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  }, []);

  // The primary relationship manager defaults to the signed-in user and stays editable (P.5).
  useEffect(() => {
    if (user && !getField("primaryCompanyRelationshipManager").value) {
      getField("primaryCompanyRelationshipManager").set("value", {
        label: `${user.firstName} ${user.lastName}`,
        value: user.emailAddress,
      });
    }
  }, [user]);

  const selectedRegion = regions.find(
    (x) => x.value === getField("franchiseID").value,
  );
  // A hint only — country is asked explicitly on wizard step 1.
  const countryHint = regionCountryHint(selectedRegion);

  const gridProps: IGridFormControlProps = {
    row: 2,
    col: 13,
    spacing: 4,
    itemResponsive: {
      lg: 6,
      md: 6,
      sm: 12,
    },
    inputControls: [
      {
        field: getField("companyName"),
        fieldKey: "companyName",
        type: EDITOR_TYPES.TEXT_FIELD,
      },
      {
        field: getField("franchiseID"),
        fieldKey: "franchiseID",
        type: EDITOR_TYPES.AUTO_COMPLETE,
        options: supportiveRegions,
      },
      {
        field: getField("primaryCompanyRelationshipManager"),
        fieldKey: "primaryCompanyRelationshipManager",
        type: EDITOR_TYPES.AUTO_COMPLETE_ASYNC,
      },
      {
        field: getField("companyType"),
        fieldKey: "companyType",
        type: EDITOR_TYPES.RADIO_BUTTON,
        options: [
          { label: "National", value: "National" },
          { label: "Regional", value: "Regional" },
        ],
      },
      {
        field: getField("leadTypeID"),
        fieldKey: "leadTypeID",
        type: EDITOR_TYPES.DROPDOWN,
        options: leadTypeList.filter((x) => x.isActive),
      },
      {
        field: getField("industryID"),
        fieldKey: "industryID",
        type: EDITOR_TYPES.DROPDOWN,
        options: industries,
      },
    ],
    form: form,
  };

  const onCancel = () => {
    // Cancel returns to the Prospects grid; nothing is created (P.5).
    navigate("/company/prospects");
  };

  const onCreate = () => {
    if (!form.isValid) {
      form.validate();
      form.showErrors();
      return;
    }

    const request: any = {
      ...form.values(),
      creationRoute: route,
      clientStatusID: 0,
      serviceTypeIDs: [],
      vendorTypeIDs: [],
      additionalCompanyRelationshipManagers: [],
      attachments: [],
      specialPricing: false,
    };

    uiStore.setGlobalLoader(true);
    CompanyService.addCompany(request)
      .pipe(finalize(() => uiStore.setGlobalLoader(false)))
      .subscribe({
        next: (data: IResult<string>) => {
          resetCompanyList();
          getProspectsCount();
          ToastService.success(data.message);
          switch (route) {
            case ClientCreationRoute.NewClientOnboarding:
              navigate(`/company/${data.content}/onboarding?step=1`);
              break;
            case ClientCreationRoute.CurrentClient:
              navigate(`/company?focus=${data.content}`);
              break;
            default:
              navigate(`/company/prospects?focus=${data.content}`);
              break;
          }
        },
        error: (error: AxiosError) => ToastService.axiosError(error),
      });
  };

  return (
    <MainContentBox>
      <Title backButtonEnabled title="Add New Client" />

      <Typography sx={{ mt: 2, mb: 1 }} variant="subtitle1">
        Which of these applies?
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        {ROUTE_OPTIONS.map((option) => (
          <Card
            key={option.route}
            variant="outlined"
            sx={{
              width: 300,
              borderColor:
                route === option.route ? "primary.main" : undefined,
              borderWidth: route === option.route ? 2 : 1,
            }}
          >
            <CardActionArea onClick={() => setRoute(option.route)}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600}>
                  {option.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* No detail form until a route is selected (P.5). */}
      {route != null && (
        <>
          <GridFormControl {...gridProps} />
          {countryHint && (
            <Typography sx={{ mt: 1 }} variant="body2" color="text.secondary">
              {countryHint}
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
            <Button variant="contained" onClick={onCreate}>
              Create Client
            </Button>
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          </Box>
        </>
      )}

      {route == null && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      )}
    </MainContentBox>
  );
};

export default observer(AddNewClient);
